<template>
  <div class="warning" :class="bgClass">
    <Icon :icon="icon" />
    <div>
      <div class="heading-texts">
        <h3 v-if="props.heading">{{ $t(props.heading) }}</h3>
        <p v-if="props.subheading">
          {{ $t(props.subheading) }}
        </p>
      </div>

      <SmartFaqLink
        v-if="props.faqLink"
        class="faq-link"
        :link="props.faqLink"
      />
      <div
        v-if="props.primaryActionText || props.secondaryActionText"
        class="actions"
      >
        <button
          v-if="props.primaryActionText"
          v-tooltip="{
            content: $t(props.primaryActionDisabledText || ''),
            hideOnTargetClick: false,
          }"
          :disabled="!!props.primaryActionDisabledText"
          class="btn btn-ghost"
          @click="emits('onPrimaryAction')"
        >
          {{ $t(props.primaryActionText) }}
        </button>
        <button
          v-if="props.secondaryActionText"
          class="link-main"
          @click="emits('onSecondaryAction')"
        >
          {{ $t(props.secondaryActionText) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export const WarningVariant = {
  ATTENTION: 'attention',
  ERROR: 'error',
  SUCCESS: 'success',
} as const;
export type WarningVariantType =
  (typeof WarningVariant)[keyof typeof WarningVariant];
</script>

<script setup lang="ts">
import type { SmartFaqLink as SmartFaqLinkType } from 'commons/smartFaq/smartFaqLinks';
import SmartFaqLink from 'commons/helpLink/SmartFaqLink.vue';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    heading?: string;
    subheading?: string;
    primaryActionText?: string;
    primaryActionDisabledText?: string;
    secondaryActionText?: string;
    faqLink?: SmartFaqLinkType;
    variant?: WarningVariantType;
  }>(),
  {
    heading: undefined,
    subheading: undefined,
    primaryActionText: undefined,
    primaryActionDisabledText: undefined,
    secondaryActionText: undefined,
    faqLink: undefined,
    variant: WarningVariant.ATTENTION,
  },
);

const emits = defineEmits(['onPrimaryAction', 'onSecondaryAction']);

const variantConfig = {
  [WarningVariant.ATTENTION]: {
    icon: 'error',
    bgClass: 'warning-attention',
  },
  [WarningVariant.ERROR]: {
    icon: 'error',
    bgClass: 'warning-error',
  },
  [WarningVariant.SUCCESS]: {
    icon: 'checkmark',
    bgClass: 'warning-success',
  },
} as const;

const icon = computed(() => variantConfig[props.variant].icon);
const bgClass = computed(() => variantConfig[props.variant].bgClass);
</script>

<style lang="scss" scoped>
@import 'src/scss/constants';
@import 'src/scss/styleguide/colors';

.warning {
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 0;
  border-radius: $border-radius-medium;
  &.warning-attention {
    background-color: $pa-color-yellow-light;
  }
  &.warning-error {
    background-color: $pa-color-red-lighter;
  }
  &.warning-success {
    background-color: $pa-color-green-light;
  }

  .icon {
    margin: 0 8px auto 0;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  button {
    font-weight: normal;
    border-width: 1px;

    &.btn-ghost {
      padding: 12px 24px;
    }

    &:disabled {
      opacity: 0.6;
    }

    color: #3b3007;
    border-color: #3b3007;
  }

  .heading-texts {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  h3 {
    margin: 0;
    line-height: 24px;
  }

  p {
    margin: 0;
  }

  .actions {
    display: flex;
    gap: 16px;
    margin-top: 16px;
  }

  .faq-link {
    margin-top: 16px;
  }
}
</style>
